import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { PersonalService } from '../../../services/modules/personal.service';
import { SnackBarService } from '../../../services/utils/snackBar.service';

export interface WorkplaceHistoryItem {
  projectName: string;
  startDate: string | Date;
  endDate?: string | Date | null;
  isCurrent?: boolean;
  isSynthetic?: boolean;
  createdDate?: string | Date | null;
  updatedDate?: string | Date | null;
  id?: number | string;
  createdByUserName?: string | null;
  updatedByUserName?: string | null;
  isEditing?: boolean;
  editStartDate?: string | Date | null;
  editEndDate?: string | Date | null;
}

export interface TransferSummary {
  EffectiveDate?: string;
  PreviousProjectId?: string;
  NewProjectId?: string;
  LeftWithoutWorkShift?: boolean;
  CanceledFutureWorkShifts?: Array<{ WorkShiftName: string; StartDate: string }>;
  CanceledDayOverrides?: string[];
  RecalculationQueued?: any[];
}

export interface DialogChangeWorkplaceData {
  employeeName: string;
  contractNumber: string | number;
  currentProjectId: string | null;
  currentProjectName: string;
  projects: Array<{ Id: string; Name: string }>;
  /** Objeto completo del contrato laboral (PersonalEmployer) tal como lo devuelve el GET. */
  contract: any;
  history?: WorkplaceHistoryItem[];
  personalCompanyInfoId?: number | string;
}

export interface DialogChangeWorkplaceResult {
  confirmed: boolean;
  updatedContract?: any;
  transferSummary?: TransferSummary;
}

function toApiDate(value: Date | string): string {
  const d = value instanceof Date ? value : new Date(value);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Los montos del contrato se guardan formateados ("$2.000.000"); el backend espera un número plano. */
function toPlainAmount(value: any): number {
  if (typeof value === 'number') { return value; }
  const cleaned = (value ?? '').toString().replace(/\$/g, '').replace(/\./g, '').replace(/,/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

@Component({
  selector: 'app-dialog-change-workplace',
  templateUrl: './dialog-change-workplace.component.html',
  styleUrls: ['./dialog-change-workplace.component.scss']
})
export class DialogChangeWorkplaceComponent implements OnInit {

  form: FormGroup;
  history: WorkplaceHistoryItem[] = [];
  saving = false;
  loadingHistory = false;
  errorMessage: string | null = null;
  minStartDate: Date;
  maxStartDate: Date;
  isAddingNew = false;

  constructor(
    private dialogRef: MatDialogRef<DialogChangeWorkplaceComponent>,
    private fb: FormBuilder,
    private personalService: PersonalService,
    private snackBService: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public data: DialogChangeWorkplaceData
  ) {}

  ngOnInit(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.minStartDate = today;
    this.maxStartDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());

    this.form = this.fb.group({
      newProjectId: [null, Validators.required],
      startDate: [null, Validators.required],
      endDate: [null]
    });

    this.loadHistory();
  }

  private loadHistory(): void {
    const personalId = this.data.personalCompanyInfoId || this.data.contract?.PersonalCompanyInfoId;
    const employerId = this.data.contract?.EmployerInfoId;

    if (!personalId || !employerId) {
      this.history = this.data.history ?? [];
      return;
    }

    this.loadingHistory = true;
    this.personalService.getWorkplaceHistory(personalId, employerId).subscribe(
      (response: any) => {
        this.loadingHistory = false;
        
        if (response && response.Contracts) {
          const currentContract = response.Contracts.find((c: any) => c.ContractNumber === this.data.contractNumber);
          if (currentContract && currentContract.Workplaces) {
            this.history = currentContract.Workplaces.map((wp: any) => ({
              id: wp.Id || wp.PersonalEmployerProjectId || wp.id,
              projectName: wp.ProjectName,
              startDate: wp.StartDate,
              endDate: wp.EndDate,
              isCurrent: wp.EndDate === null,
              isSynthetic: wp.IsSynthetic,
              createdDate: wp.CreateDate,
              updatedDate: wp.UpdateDate,
              createdByUserName: wp.CreatedByUserName,
              updatedByUserName: wp.UpdatedByUserName
            })).reverse();
          }
        }
      },
      (error: HttpErrorResponse) => {
        this.loadingHistory = false;
        // Fallback to data.history if api fails
        this.history = this.data.history ?? [];
      }
    );
  }

  private parseDateStr(d: any): Date | null {
    if (!d) return null;
    if (d instanceof Date) return d;
    const parts = d.split('-');
    if (parts.length >= 3) {
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2].substring(0, 2), 10));
    }
    return new Date(d);
  }

  editRow(row: WorkplaceHistoryItem): void {
    if (row.isSynthetic) return;
    row.isEditing = true;
    row.editStartDate = this.parseDateStr(row.startDate);
    row.editEndDate = row.endDate ? this.parseDateStr(row.endDate) : null;
  }

  cancelEdit(row: WorkplaceHistoryItem): void {
    row.isEditing = false;
    row.editStartDate = null;
    row.editEndDate = null;
  }

  saveEdit(row: WorkplaceHistoryItem): void {
    if (!row.editStartDate) {
      this.snackBService.openWarning('La fecha de inicio es requerida.', '', 4000);
      return;
    }

    const start = this.parseDateStr(row.editStartDate);
    const end = row.editEndDate ? this.parseDateStr(row.editEndDate) : null;

    if (end && start && end < start) {
      this.snackBService.openWarning('La fecha de fin no puede ser anterior a la fecha de inicio', '', 4000);
      return;
    }

    const startTime = start.getTime();
    const endTime = end ? end.getTime() : Infinity;

    const overlap = this.history.some(h => {
      if (h === row || h.isSynthetic) return false;
      const hStart = this.parseDateStr(h.startDate);
      const hEnd = h.endDate ? this.parseDateStr(h.endDate) : null;
      
      const hStartTime = hStart ? hStart.getTime() : -Infinity;
      const hEndTime = hEnd ? hEnd.getTime() : Infinity;

      if (startTime > hEndTime || hStartTime > endTime) {
        return false;
      }
      return true;
    });

    if (overlap) {
      this.snackBService.openWarning('La vigencia se solapa con otra asignación del historial', '', 5000);
      return;
    }

    if (!end) {
      const hasOtherOpen = this.history.some(h => h !== row && !h.isSynthetic && !h.endDate);
      if (hasOtherOpen) {
        this.snackBService.openWarning('El contrato ya tiene una asignación abierta', '', 5000);
        return;
      }
    }

    const contract = this.data.contract || {};
    const personalEmployerId = contract.Id;

    if (!personalEmployerId) {
       this.snackBService.openWarning('Falta el ID del contrato.', '', 4000);
       return;
    }

    if (!row.id) {
       this.snackBService.openWarning('No se puede editar: El backend no está devolviendo el Id de la asignación en el GET (ver documentación).', '', 6000);
       return;
    }

    this.saving = true;

    const body = {
      StartDate: toApiDate(start),
      EndDate: end ? toApiDate(end) : null
    };

    this.personalService.putWorkplaceHistory(personalEmployerId, row.id, body, false).subscribe(
      (response: any) => {
        this.saving = false;
        row.isEditing = false;
        
        if (response && response.Workplace) {
          row.startDate = response.Workplace.StartDate;
          row.endDate = response.Workplace.EndDate;
          row.isCurrent = response.Workplace.EndDate === null;
        }

        if (response && response.Warnings && response.Warnings.length > 0) {
          const gapWarning = response.Warnings.find((w: any) => w.Code === 'GAP');
          if (gapWarning) {
            this.snackBService.openWarning(gapWarning.Message, '', 8000);
          } else {
            this.snackBService.openWarning(response.Warnings[0].Message, '', 8000);
          }
        } else {
           this.snackBService.openSuccess('Asignación actualizada correctamente.', '', 4000);
        }
      },
      (error: HttpErrorResponse) => {
        this.saving = false;
        console.error('Error desde la API putWorkplaceHistory:', error);
        const msg = error?.error?.Error || error?.error?.Message || 'No fue posible actualizar la asignación.';
        this.snackBService.openWarning(msg, '', 6000);
      }
    );
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false } as DialogChangeWorkplaceResult);
  }

  onAddNew(): void {
    this.isAddingNew = true;
    this.form.reset();
  }

  cancelAddNew(): void {
    this.isAddingNew = false;
    this.form.reset();
  }

  saveNewWorkplace(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const contract = this.data.contract || {};
    const salary = toPlainAmount(contract.Salary);

    if (!salary) {
      this.snackBService.openWarning(
        'No puedes cambiar el lugar de trabajo porque la persona no tiene salario mensual asignado. ' +
        'Asigna un salario en el contrato antes de continuar.',
        '', 6000
      );
      return;
    }

    this.errorMessage = null;
    this.saving = true;

    const body = {
      ContractNumber: contract.ContractNumber,
      EmployerInfoId: contract.EmployerInfoId,
      ContractTypeId: contract.ContractTypeId,
      StartDate: contract.StartDate,
      EndDate: contract.EndDate,
      PositionId: contract.PositionId,
      PositionName: contract.PositionName,
      Salary: salary,
      TransportationAllowance: toPlainAmount(contract.TransportationAllowance),
      PayPeriodId: contract.PayPeriodId,
      BankId: contract.BankId,
      BankAccountTypeId: contract.BankAccountTypeId,
      BankAccount: contract.BankAccount,
      IsActive: contract.IsActive,
      HasExtraTime: contract.HasExtraTime,
      ProjectId: this.form.get('newProjectId').value,
      ProjectEffectiveDate: toApiDate(this.form.get('startDate').value),
      ProjectEndDate: this.form.get('endDate').value ? toApiDate(this.form.get('endDate').value) : null,
      WorkShiftId: null
    };

    this.personalService.putPersonalEmployer(contract.Id, body).subscribe((updatedContract: any) => {
      this.saving = false;
      const transferSummary: TransferSummary = updatedContract?.TransferSummary;
      this.notifyResult(transferSummary);
      this.dialogRef.close({ confirmed: true, updatedContract, transferSummary } as DialogChangeWorkplaceResult);
    }, (error: HttpErrorResponse) => {
      this.saving = false;
      this.errorMessage = error?.error?.Error || error?.error?.Message || 'No fue posible cambiar el lugar de trabajo.';
    });
  }

  private notifyResult(summary?: TransferSummary): void {
    if (!summary) {
      this.snackBService.openSuccess('Lugar de trabajo actualizado correctamente.', '', 4000);
      return;
    }

    if (summary.LeftWithoutWorkShift) {
      this.snackBService.openWarning(
        'El lugar de trabajo se actualizó, pero el contrato quedó sin turno asignado. ' +
        'Asigna un turno del nuevo proyecto para que esos días entren a la prenómina.',
        '', 8000
      );
    } else {
      this.snackBService.openSuccess(
        `Lugar de trabajo actualizado. Aplica desde ${summary.EffectiveDate}. ` +
        'Las prenóminas afectadas se están recalculando en segundo plano.',
        '', 5000
      );
    }
  }
}
